import { EditFormWrapper } from "@/features/Edit/EditFormWrapper.tsx";
import { getAuthorized } from "@/lib/fetch.tsx";
import { Card, CardHeader, Divider } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { NodeData } from "@/api_model/Api.ts";
import { Label } from "@/components/Form";
import { a7FIAInstrumentsEditForm } from "@/pages/a7/EditForms/a7FIAInstrumentsEditForm.tsx";
import { a7FIAInstrumentTSEditForm } from "@/pages/a7/EditForms/a7FIAInstrumentTSEditForm.tsx";

export interface A7FIAInstrumentMultiEditCardProps {
  instrument_id: number;
  selectedDate?: string;
  set_issuer_id: (issuer_id: number) => void;
  set_instrument_type: (instrument_type: string) => void;
}

export function A7FIAInstrumentMultiEditCard({
  instrument_id,
  selectedDate,
  set_issuer_id,
  set_instrument_type,
}: A7FIAInstrumentMultiEditCardProps) {
  const fieldChangeCallBack = useMemo(
    () => ({
      a7_fia_issuer_id: set_issuer_id,
      type: set_instrument_type,
    }),
    [set_issuer_id, set_instrument_type],
  );

  const { data: instrument_ts_id } = useQuery({
    queryKey: [
      "a7",
      "fia_multi_edit",
      "instrument_ts_id",
      instrument_id,
      selectedDate,
    ],
    queryFn: async () => {
      return getAuthorized(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/a7/fia_multi_edit/instrument_ts_id/${instrument_id}/${selectedDate}`,
      ).then((response) => response.json() as Promise<number | undefined>);
    },
    enabled: instrument_id != undefined && selectedDate != undefined,
    refetchOnWindowFocus: false,
  });

  const { data: instrument_fund_list } = useQuery({
    queryKey: [
      "a7",
      "fia_multi_edit",
      "instrument_fund_list",
      instrument_id,
      selectedDate,
    ],
    queryFn: async () => {
      return getAuthorized(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/a7/fia_multi_edit/instrument_fund_list/${instrument_id}/${selectedDate}`,
      ).then((response) => response.json() as Promise<NodeData[] | undefined>);
    },
    enabled: instrument_id != undefined && selectedDate != undefined,
    refetchOnWindowFocus: false,
  });

  return (
    <Card onClick={(e) => e.stopPropagation()}>
      <CardHeader className="text-lg font-bold text-gray-700">
        <Label className="w-1/2 text-xl" name="Instrument financier" />
        {instrument_fund_list && (
          <div className="w-1/2 flex flex-row-reverse">
            {instrument_fund_list.length === 1 ? (
              <Label className="text-xs" name={"Contenu dans 1 seul fonds"} />
            ) : (
              <Label
                className="text-xs text-red-600 "
                name={`Contenu dans ${instrument_fund_list.length} fonds`}
              />
            )}
          </div>
        )}
      </CardHeader>
      <Divider />
      <div className="flex flex-col p-2">
        <EditFormWrapper
          client={"a7"}
          id={Number(instrument_id)}
          allowDelete={false}
          multiFieldOptions={{
            a7_fia_issuer_id: { isVisible: false },
            a7_fia_issuer_name: { isVisible: false },
          }}
          fieldChangeCallBack={fieldChangeCallBack}
          {...a7FIAInstrumentsEditForm}
        />
        {instrument_ts_id ? (
          <EditFormWrapper
            client={"a7"}
            id={instrument_ts_id}
            allowDelete={false}
            multiFieldOptions={{
              date: { isVisible: false },
              a7_fia_instrument_id: { isVisible: false },
            }}
            {...a7FIAInstrumentTSEditForm}
          />
        ) : (
          <div>Pas de série temporelle pour cet instrument / date.</div>
        )}
      </div>
    </Card>
  );
}
